import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Globe, Building2, Loader2 } from 'lucide-react';
import { useEnrichByUrl, useEnrichByCnpj } from '@/api/mutations/empresa';
import { toast } from 'sonner';

interface EnrichModalProps {
  empresaId: string;
  empresaNome: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultUrl?: string;
  defaultCnpj?: string;
}

export function EnrichModal({
  empresaId,
  empresaNome,
  open,
  onOpenChange,
  defaultUrl,
  defaultCnpj,
}: EnrichModalProps) {
  const [tab, setTab] = useState<'url' | 'cnpj'>(defaultUrl ? 'url' : 'cnpj');
  const [url, setUrl] = useState(defaultUrl ?? '');
  const [cnpj, setCnpj] = useState(defaultCnpj ?? '');
  const enrichUrl = useEnrichByUrl();
  const enrichCnpj = useEnrichByCnpj();

  async function submit() {
    if (tab === 'url') {
      if (!url.trim()) {
        toast.error('Informe a URL');
        return;
      }
      try {
        const res = await enrichUrl.mutateAsync({ id: empresaId, url: url.trim() });
        const found = res.found?.redes ?? {};
        const count = Object.keys(found).length;
        toast.success(
          count > 0 ? `Encontradas ${count} redes sociais` : 'Site salvo (sem redes detectadas)',
        );
        onOpenChange(false);
      } catch (e) {
        toast.error('Falha no enrich URL: ' + (e instanceof Error ? e.message : 'erro'));
      }
    } else {
      const clean = cnpj.replace(/\D/g, '');
      if (clean.length !== 14) {
        toast.error('CNPJ deve ter 14 dígitos');
        return;
      }
      try {
        const res = await enrichCnpj.mutateAsync({ id: empresaId, cnpj: clean });
        toast.success(`BrasilAPI: ${res.found?.razao_social ?? 'dados atualizados'}`);
        onOpenChange(false);
      } catch (e) {
        toast.error('Falha no enrich CNPJ: ' + (e instanceof Error ? e.message : 'erro'));
      }
    }
  }

  const loading = enrichUrl.isPending || enrichCnpj.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Enriquecer empresa</h3>
            <p className="text-sm text-muted-foreground">{empresaNome}</p>
          </div>

          <div className="flex gap-1 border-b">
            <button
              type="button"
              onClick={() => setTab('url')}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                tab === 'url'
                  ? 'border-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Globe className="h-4 w-4" />
              Via URL do site
            </button>
            <button
              type="button"
              onClick={() => setTab('cnpj')}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                tab === 'cnpj'
                  ? 'border-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Building2 className="h-4 w-4" />
              Via CNPJ (BrasilAPI)
            </button>
          </div>

          {tab === 'url' ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">URL do site</label>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://avrasementes.com.br/"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
              />
              <p className="text-xs text-muted-foreground">
                Vou baixar o HTML e extrair Instagram, LinkedIn, YouTube, Facebook, Twitter/X e
                TikTok via regex. Os links das redes encontradas viram o campo{' '}
                <code className="text-xs">redes</code>.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">CNPJ</label>
              <Input
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0001-00"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
              />
              <p className="text-xs text-muted-foreground">
                BrasilAPI gratuita. Preenche: razão social, endereço completo, CEP, telefone da
                Receita, e mapeia CNAE pro setor.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Enriquecer
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
